import {
  HTTPRequest,
  HTTPResponse,
  Auth as NuxtAuth,
  RefreshSchemeOptions,
  SchemePartialOptions,
} from "@nuxtjs/auth-next";
import { Auth, ExpiredAuthSessionError, RefreshScheme } from "@nuxtjs/auth-next/dist/runtime";
import axios, { AxiosRequestConfig } from "axios";
import { type Route } from "vue-router";

import { $config } from "@/utils/accessors/config";
import cookieDomain from "@/utils/cookie-domain";
import * as base64 from "@/utils/encoding/base64";

/**
 * Add type hinting to the application that an authenticated user is of type AuthUserSigil
 */
declare module "vue/types/vue" {
  interface Auth extends NuxtAuth {
    user: (AuthUserSigil & NuxtAuth["user"]) | (Auth0UserTokenResponse & NuxtAuth["user"]) | null;
  }
}

declare module "@nuxt/types" {
  interface Auth extends NuxtAuth {
    user: (AuthUserSigil & NuxtAuth["user"]) | (Auth0UserTokenResponse & NuxtAuth["user"]) | null;
  }
}

declare module "vuex/types/index" {
  interface Auth extends NuxtAuth {
    user: (AuthUserSigil & NuxtAuth["user"]) | (Auth0UserTokenResponse & NuxtAuth["user"]) | null;
  }
}

const formEncode = (data: Record<string, string | number | boolean>) =>
  Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");

const parseToken = (token: string) => {
  const encodedData = token.split(".")[1];
  const decodedData = base64.decodeUtf8(encodedData);

  return JSON.parse(decodedData);
};

interface SigilSchemeOptions extends RefreshSchemeOptions {
  cookie?: {
    prefix?: string;
    options?: {
      path?: string;
      expires?: number | Date;
      maxAge?: number;
      domain?: string;
      secure?: boolean;
    };
  };
  idToken: {
    property: string;
    prefix: string;
  };
}

const DEFAULTS: SchemePartialOptions<SigilSchemeOptions> = {
  name: "sigil",
  idToken: {
    property: "id_token",
    prefix: "_id_token.",
  },
};

export const isSigilUserToken = (user: AuthUserSigil | Auth0UserTokenResponse): user is AuthUserSigil => {
  return "jwt" in user;
};

export default class SigilScheme<OptionsT extends SigilSchemeOptions> extends RefreshScheme<OptionsT> {
  constructor($auth: Auth, options: SchemePartialOptions<SigilSchemeOptions>) {
    // Create dynamic cookie domain based on runtime config
    if (options.cookie) {
      const hostname = $config.ltkHost;
      const domain = cookieDomain(hostname);

      if ($auth.options.cookie) {
        $auth.options.cookie.options = Object.assign({}, $auth.options.cookie.options, options.cookie.options, {
          domain,
        });

        if ($config.env !== "prod") {
          const envPrefix = `${$config.env}.`;

          if (!$auth.options.cookie.prefix.startsWith(envPrefix)) {
            $auth.options.cookie.prefix = `${envPrefix}${$auth.options.cookie.prefix}`;
          }

          if ($auth.options.localStorage !== false && !$auth.options.localStorage.prefix.startsWith(envPrefix)) {
            $auth.options.localStorage.prefix = `${envPrefix}${$auth.options.localStorage.prefix}`;
          }
        }
      }
    }

    options.idToken = {
      ...DEFAULTS.idToken,
      ...(options.idToken ?? {}),
    };

    super($auth, options as OptionsT);
  }

  check(checkStatus = false) {
    this.$auth.$storage.syncUniversal(this.options.idToken.prefix + this.options.name);

    return super.check(checkStatus);
  }

  /**
   * Overload the original implementation so that it can support form-encoded data
   */
  async login(endpoint: AxiosRequestConfig, { reset = true } = {}): Promise<HTTPResponse> {
    if (!this.options.endpoints.login) {
      return undefined as never;
    }
    if (reset) {
      this.$auth.reset({ resetInterceptor: false });
    }

    if (this.options.clientId) {
      endpoint.data.client_id = this.options.clientId;
    }
    if (this.options.grantType) {
      endpoint.data.grant_type = this.options.grantType;
    }
    if (this.options.scope) {
      endpoint.data.scope = this.options.scope;
    }
    // Form Encode Data
    endpoint.data = formEncode(endpoint.data);
    endpoint.baseURL = `https://${$config.authHost}`;

    try {
      const response = await this.$auth.request(endpoint, this.options.endpoints.login);
      this.updateTokens(response);

      if (!this.requestHandler.interceptor) {
        this.initializeRequestInterceptor();
      }
      if (this.options.user.autoFetch) {
        await this.fetchUser();
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async forgotPassword(endpoint: AxiosRequestConfig): Promise<HTTPResponse | void> {
    if (!this.options.endpoints.forgotPassword) {
      return;
    }

    // Form Encode Data
    endpoint.data = formEncode(endpoint.data);
    endpoint.baseURL = `https://${$config.authHost}`;

    try {
      const response = await this.$auth.request(endpoint, this.options.endpoints.forgotPassword as AxiosRequestConfig);
      return response;
    } catch (error) {
      let message = "";
      if (error !== null && typeof error === "object") {
        if (axios.isAxiosError(error)) {
          message = error.response?.data?.developer_message as string;
        } else if ("message" in error) {
          message = error.message as string;
        }
      }

      return Promise.reject(new Error(message));
    }
  }

  async signup(endpoint: AxiosRequestConfig): Promise<HTTPResponse | void> {
    if (!this.options.endpoints.signup) {
      return;
    }

    // Form Encode Data
    endpoint.data = formEncode(endpoint.data);
    endpoint.baseURL = `https://${$config.authHost}`;

    try {
      const response = await this.$auth.request(endpoint, this.options.endpoints.signup as AxiosRequestConfig);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Overload the original implementation so that it can support form-encoded data
   */
  refreshTokens() {
    // Refresh endpoint is disabled
    if (!this.options.endpoints.refresh) {
      return Promise.resolve();
    }

    // Token and refresh token are required but not available
    if (!this.check().valid) {
      return Promise.resolve();
    }

    // Get refresh token status
    const refreshTokenStatus = this.refreshToken.status();

    // Refresh token is expired. There is no way to refresh. Force reset.
    if (refreshTokenStatus.expired()) {
      this.$auth.reset();

      throw new ExpiredAuthSessionError();
    }

    // Delete current token from the request header before refreshing, if `tokenRequired` is disabled
    if (!this.options.refreshToken.tokenRequired) {
      this.requestHandler.clearHeader();
    }

    const endpoint: AxiosRequestConfig = {
      data: {
        client_id: undefined,
        grant_type: undefined,
      },
    };

    // Add refresh token to payload if required
    if (this.options.refreshToken.required && this.options.refreshToken.data) {
      endpoint.data[this.options.refreshToken.data] = this.refreshToken.get();
    }

    // Add client id to payload if defined
    if (this.options.clientId) {
      endpoint.data.client_id = this.options.clientId;
    }

    // Add grant type to payload if defined
    if (this.options.grantType) {
      endpoint.data.grant_type = "refresh_token";
    }

    // Form Encode Data
    endpoint.data = endpoint.data = formEncode(endpoint.data);
    endpoint.baseURL = `https://${$config.authHost}`;

    // Make refresh request
    return this.$auth
      .request(endpoint, this.options.endpoints.refresh)
      .then((response) => {
        // Update tokens
        this.updateTokens(response, { isRefreshing: true });

        return response;
      })
      .catch((error) => {
        this.$auth.callOnError(error, { method: "refreshToken" });
        return Promise.reject(error);
      });
  }

  fetchUser(endpoint?: HTTPRequest): Promise<HTTPResponse | void> {
    // Token is required but not available
    if (!this.check().valid) {
      return Promise.resolve();
    }

    // User endpoint is disabled
    if (!this.options.endpoints.user) {
      this.$auth.setUser({});
      return Promise.resolve();
    }

    endpoint = endpoint ?? this.options.endpoints.user;
    endpoint.baseURL = `https://${$config.authHost}`;

    // Try to fetch user and then set
    return this.$auth
      .requestWith(this.name, endpoint, this.options.endpoints.user)
      .then((response) => {
        const userData =
          this.options.user.property === false ? response.data : response.data[this.options.user.property];

        if (!userData) {
          const error = new Error(`User Data response does not contain field ${this.options.user.property}`);
          return Promise.reject(error);
        }

        // Embed decoded JWT in user profile if a token exists.
        const idToken = this.$auth.$storage.getUniversal(this.options.idToken.prefix + this.options.name);
        if (idToken !== undefined && typeof idToken === "string") {
          userData.jwt = parseToken(idToken);
        }

        this.$auth.setUser(userData);
        return response;
      })
      .catch((error) => {
        this.$auth.callOnError(error, { method: "fetchUser" });
        return Promise.reject(error);
      });
  }

  changePassword(endpoint: AxiosRequestConfig): Promise<HTTPResponse | void> {
    if (!this.options.endpoints.changePassword) {
      return Promise.resolve();
    }

    // Form Encode Data
    endpoint.data = formEncode(endpoint.data);
    endpoint.baseURL = `https://${$config.authHost}`;

    return this.$auth
      .request(endpoint, this.options.endpoints.changePassword as AxiosRequestConfig)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return Promise.reject(new Error(error?.response?.data?.developer_message || error.message));
      });
  }

  async changeEmail(endpoint: AxiosRequestConfig): Promise<HTTPResponse | void> {
    if (!this.options.endpoints.changeEmail) {
      return Promise.resolve();
    }

    // Form Encode Data
    endpoint.data = formEncode(endpoint.data);
    endpoint.baseURL = `https://${$config.authHost}`;

    try {
      const response = await this.$auth.request(endpoint, this.options.endpoints.changeEmail as AxiosRequestConfig);
      await this.fetchUser();
      return response;
    } catch (error) {
      let message = "";
      if (error !== null && typeof error === "object") {
        if (axios.isAxiosError(error)) {
          message = error.response?.data?.developer_message as string;
        } else if ("message" in error) {
          message = error.message as string;
        }
      }

      return Promise.reject(new Error(message));
    }
  }

  async logout(endpoint: HTTPRequest = {}) {
    // Only connect to logout endpoint if it's configured
    if (this.options.endpoints.logout) {
      endpoint.baseURL = `https://${$config.authHost}`;

      await this.$auth.requestWith(this.name, endpoint, this.options.endpoints.logout).catch(() => {
        //
      });
    }

    // But reset regardless
    return this.$auth.reset();
  }

  reset({ resetInterceptor = true } = {}) {
    this.$auth.$storage.removeUniversal(`${this.options.idToken.prefix}${this.options.name}`);

    super.reset({ resetInterceptor });
  }

  async loginRedirectFlow(query: Route["query"]) {
    const { access_token: accessToken, id_token: idToken } = query;

    // Bad login redirect
    if (typeof accessToken !== "string" || typeof idToken !== "string" || accessToken === "" || idToken === "") {
      return this.$auth.redirect(this.$auth.options.redirect.login);
    }

    // Set the id token
    this.$auth.$storage.setUniversal(`${this.options.idToken.prefix}${this.options.name}`, idToken);

    // Manually set tokens. There is no refresh token in the legacy redirect flow, or impersonation flow
    await this.$auth.setUserToken(accessToken, "legacy-token");
  }

  protected updateTokens(response: HTTPResponse, { isRefreshing = false, updateOnRefresh = true } = {}): void {
    const idToken = response.data[this.options.idToken.property];

    this.$auth.$storage.setUniversal(`${this.options.idToken.prefix}${this.options.name}`, idToken);

    super.updateTokens(response, { isRefreshing, updateOnRefresh });
  }
}
